/**
 * Platform Reports Hub — two-panel layout:
 *   - Left rail: category menu with live match counts
 *   - Main panel: category sections showing Exceptions + Reports
 * Category selection is persisted in the URL via ?category=<slug>.
 */
import React, { useMemo, useState } from 'react'
import {
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  List,
  Divider,
  Paper,
} from '@mui/material'
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  DashboardOutlined as AllReportsIcon,
  Star as FavouritesIcon,
  LabelOutlined as AllTypesIcon,
} from '@mui/icons-material'
import { Link, useSearchParams } from 'react-router-dom'
import { useDataFetching } from '../../../hooks/useDataFetching'
import { platformReportsService, ReportType } from '../../../services/platformReportsService'
import {
  CATEGORY_ORDER,
  categoryToSlug,
  slugToCategory,
  getCategoryIcon,
  getCategoryColor,
  buildExceptionRows,
  rowSearchHaystack,
  ANALYSIS_META,
} from './components/reportHelpers'
import {
  expandSearchTerm,
  haystackIncludesAnyTerm,
} from '../../../utils/exceptionSearchAliases'
import FpVisibilityIcon from '../../../components/inspector/shared/FpVisibilityIcon'
import ExceptionTypeLabel from '../../../components/shared/ExceptionTypeLabel'
import { RailItem, RAIL_SECTION_LABEL_SX } from '../../../components/shared/FilterRail'
import { EXCEPTION_TYPE_META } from '../../../components/SystemIcons'
import { poolwerxColors } from '../../../theme/colors'
import FavouriteStar from './FavouriteStar'
import { usePlatformReportFavourites } from './PlatformReportFavourites'
import AdminPageHeader from '../../../components/admin/shared/AdminPageHeader'

const ALL = 'all'
const FAVOURITES = 'favourites'
const TYPE_ALL = 'all'
const linkStyle = { textDecoration: 'none', color: 'inherit', display: 'block' } as const

function RowLink({ report }: { report: ReportType }) {
  const isException = !!report.is_exception_report
  const AnalysisGlyph = ANALYSIS_META.icon
  return (
    <Paper
      variant="outlined"
      sx={{
        mb: 1,
        borderRadius: 2,
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': {
          borderColor: 'primary.main',
          bgcolor: 'action.hover',
          '& .report-name': { color: 'primary.main' },
        },
      }}
    >
      <Link to={`/admin/platform-reports/${report.url_slug}`} style={linkStyle}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            px: 2.5,
            py: 1.75,
            cursor: 'pointer',
          }}
        >
          <FavouriteStar id={report.id} />
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              className="report-name"
              sx={{
                fontWeight: 600,
                fontSize: '0.95rem',
                transition: 'color 0.15s',
                mb: 0.25,
              }}
            >
              {report.name}
            </Typography>
            {report.description && (
              <Typography variant="body2" color="text.secondary">
                {report.description}
              </Typography>
            )}
          </Box>
          {isException && (
            <FpVisibilityIcon
              kind="exception"
              visibleToRoles={report.visible_to_roles}
              variant="chip"
            />
          )}
          {isException && report.exception_type ? (
            // Exception: soft-filled pill in the shared type taxonomy
            // (teal = data, purple = process) with its glyph.
            <ExceptionTypeLabel
              exceptionType={report.exception_type}
              variant="pill"
              iconSize={14}
              sx={{ flexShrink: 0 }}
            />
          ) : !isException ? (
            // Analysis report: soft-filled blue pill with the QueryStats glyph,
            // matching the exception pills' chrome but a distinct hue.
            <Chip
              icon={<AnalysisGlyph sx={{ fontSize: 14 }} />}
              label={ANALYSIS_META.label}
              size="small"
              sx={{
                flexShrink: 0,
                height: 22,
                pl: '6px',
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: 0.3,
                color: ANALYSIS_META.color,
                backgroundColor: ANALYSIS_META.bgColor,
                '& .MuiChip-icon': { color: 'inherit', fontSize: 14 },
              }}
            />
          ) : (
            // Unclassified exception (no type assigned yet): neutral chip.
            <Chip
              label="Exception"
              size="small"
              sx={{
                flexShrink: 0,
                height: 22,
                fontSize: '0.7rem',
                fontWeight: 600,
                letterSpacing: 0.3,
                color: 'text.secondary',
              }}
            />
          )}
        </Box>
      </Link>
    </Paper>
  )
}

function CategorySection({ category, rows }: { category: string; rows: ReportType[] }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          mb: 1.5,
          pb: 1,
          borderBottom: 2,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ color: `${getCategoryColor(category)}.main`, display: 'flex' }}>
          {getCategoryIcon(category)}
        </Box>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {category}
        </Typography>
      </Box>
      {rows.map((r) => <RowLink key={r.id} report={r} />)}
    </Box>
  )
}

export default function PlatformReportsHub() {
  const { data: payload, loading, error } = useDataFetching({
    fetchFn: () => platformReportsService.listReportTypes(),
  })
  const reportTypes = payload?.reportTypes
  const searchAliases = payload?.searchAliases
  const { favourites } = usePlatformReportFavourites()
  const [search, setSearch] = useState('')
  const [searchParams] = useSearchParams()

  const selectedCategory = useMemo(() => {
    const slug = searchParams.get('category')
    if (!slug) return ALL
    if (slug === FAVOURITES) return FAVOURITES
    return slugToCategory(slug) ?? ALL
  }, [searchParams])

  // Category + Favourites share one single-select axis (?category=). Rail items
  // are real <Link>s; a relative href (vs setSearchParams) keeps right-click /
  // open-in-new-tab working and never remounts the hub, so the typed search
  // term survives a rail click. Toggling one axis preserves the other (?type=).
  const categoryHref = (category: string): string => {
    const next = new URLSearchParams(searchParams)
    if (category === ALL) next.delete('category')
    else if (category === FAVOURITES) next.set('category', FAVOURITES)
    else next.set('category', categoryToSlug(category))
    const s = next.toString()
    return s ? `?${s}` : ''
  }

  // Type filter (All / Data Discrepancy / Process Issue / Analysis),
  // bookmarked via ?type=. "analysis" = a non-exception (analysis) report.
  const selectedType = useMemo(() => {
    const t = searchParams.get('type')
    return t === 'data_discrepancy' || t === 'process_issue' || t === 'analysis'
      ? t
      : TYPE_ALL
  }, [searchParams])

  // Type is the independent axis (?type=).
  const typeHref = (type: string): string => {
    const next = new URLSearchParams(searchParams)
    if (type === TYPE_ALL) next.delete('type')
    else next.set('type', type)
    const s = next.toString()
    return s ? `?${s}` : ''
  }

  const allReports = useMemo(() => {
    if (!reportTypes) return []
    const operational = reportTypes
      .filter((r) => !r.is_exception_report)
      .map((r) => ({ ...r, searchHaystack: rowSearchHaystack(r) }))
    const exceptionRows = buildExceptionRows(reportTypes)
    return [...operational, ...exceptionRows]
  }, [reportTypes])

  const matched = useMemo(() => {
    const term = search.toLowerCase().trim()
    if (!term) return allReports
    const expandedTerms = expandSearchTerm(term, searchAliases)
    return allReports.filter((r) =>
      haystackIncludesAnyTerm(r.searchHaystack ?? rowSearchHaystack(r), expandedTerms),
    )
  }, [allReports, search, searchAliases])

  const counts = useMemo(() => {
    const map: Record<string, number> = {}
    for (const r of matched) map[r.category] = (map[r.category] ?? 0) + 1
    return map
  }, [matched])

  // Favourites present under the current search (mirrors how category counts
  // track `matched`). `favouritesTotal` ignores search so the empty state can
  // tell "no favourites at all" from "none match this search".
  const favouritesCount = useMemo(
    () => matched.filter((r) => favourites.has(r.id)).length,
    [matched, favourites],
  )
  const favouritesTotal = useMemo(
    () => allReports.filter((r) => favourites.has(r.id)).length,
    [allReports, favourites],
  )

  // Rows in the current category/search scope, before the Kind filter -
  // drives both the Kind segmented counts and the visible sections.
  const categoryMatched = useMemo(() => {
    if (selectedCategory === ALL) return matched
    if (selectedCategory === FAVOURITES) return matched.filter((r) => favourites.has(r.id))
    return matched.filter((r) => r.category === selectedCategory)
  }, [matched, selectedCategory, favourites])

  const typeCounts = useMemo(
    () => ({
      all: categoryMatched.length,
      data_discrepancy: categoryMatched.filter(
        (r) => r.is_exception_report && r.exception_type === 'data_discrepancy',
      ).length,
      process_issue: categoryMatched.filter(
        (r) => r.is_exception_report && r.exception_type === 'process_issue',
      ).length,
      analysis: categoryMatched.filter((r) => !r.is_exception_report).length,
    }),
    [categoryMatched],
  )

  const visibleSections = useMemo(() => {
    let visible = categoryMatched
    if (selectedType === 'data_discrepancy')
      visible = visible.filter(
        (r) => r.is_exception_report && r.exception_type === 'data_discrepancy',
      )
    else if (selectedType === 'process_issue')
      visible = visible.filter(
        (r) => r.is_exception_report && r.exception_type === 'process_issue',
      )
    else if (selectedType === 'analysis')
      visible = visible.filter((r) => !r.is_exception_report)
    const grouped = new Map<string, ReportType[]>()
    for (const r of visible) {
      const list = grouped.get(r.category) || []
      list.push(r)
      grouped.set(r.category, list)
    }
    for (const list of grouped.values()) {
      list.sort((a, b) => a.name.localeCompare(b.name))
    }
    return CATEGORY_ORDER
      .filter((c) => grouped.has(c))
      .map((c) => [c, grouped.get(c)!] as [string, ReportType[]])
  }, [categoryMatched, selectedType])

  // Type rail options: All types + the two classifications + Analysis. Icons /
  // colours come from the single source (EXCEPTION_TYPE_META / ANALYSIS_META);
  // All types uses a neutral glyph. Counts already reflect the category + search.
  const typeRailOptions: Array<{
    value: string
    label: string
    count: number
    color?: string
    Icon: React.ElementType
  }> = [
    { value: TYPE_ALL, label: 'All types', count: typeCounts.all, Icon: AllTypesIcon },
    {
      value: 'data_discrepancy',
      label: EXCEPTION_TYPE_META.data_discrepancy.label,
      count: typeCounts.data_discrepancy,
      color: EXCEPTION_TYPE_META.data_discrepancy.color,
      Icon: EXCEPTION_TYPE_META.data_discrepancy.icon ?? AllTypesIcon,
    },
    {
      value: 'process_issue',
      label: EXCEPTION_TYPE_META.process_issue.label,
      count: typeCounts.process_issue,
      color: EXCEPTION_TYPE_META.process_issue.color,
      Icon: EXCEPTION_TYPE_META.process_issue.icon ?? AllTypesIcon,
    },
    {
      value: 'analysis',
      label: ANALYSIS_META.label,
      count: typeCounts.analysis,
      color: ANALYSIS_META.color,
      Icon: ANALYSIS_META.icon,
    },
  ]

  return (
    <>
      <AdminPageHeader pageKey="platformReports" />

      {reportTypes && reportTypes.length > 0 && (
        <TextField
          fullWidth
          size="small"
          placeholder="Search reports..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
          sx={{
            mb: 2,
            '& .MuiOutlinedInput-root': {
              borderRadius: 1.5,
              bgcolor: 'background.paper',
              '& fieldset': { borderColor: 'grey.400' },
              '&:hover fieldset': { borderColor: 'grey.500' },
              '&.Mui-focused fieldset': { borderColor: poolwerxColors.teal },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearch('')}>
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      )}

      {loading && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {reportTypes && reportTypes.length > 0 && (
        <>
          <Box sx={{ display: 'flex', gap: 3, alignItems: 'flex-start' }}>
            {/* Left rail — Favourites, then Area, then Type. Three labelled
                sections matching the Exceptions hub; each item is a real <Link>
                so right-click / open-in-new-tab work. Favourites + Area are one
                single-select axis (?category=); Type is independent (?type=). */}
            <Box
              sx={{
                width: 320,
                flexShrink: 0,
                display: { xs: 'none', md: 'block' },
                position: 'sticky',
                top: 16,
              }}
            >
              <Paper
                variant="outlined"
                sx={{ borderRadius: 2, borderColor: 'grey.200', py: 1, px: 0.75 }}
              >
                {/* Favourites is a single pinned shortcut at the top - no
                    heading (it would just stutter the item label). */}
                <List disablePadding data-testid="favourites-rail">
                  <RailItem
                    to={categoryHref(FAVOURITES)}
                    active={selectedCategory === FAVOURITES}
                    icon={<FavouritesIcon sx={{ fontSize: 20, color: poolwerxColors.teal }} />}
                    label="Favourites"
                    count={favouritesCount}
                  />
                </List>

                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" sx={RAIL_SECTION_LABEL_SX}>
                  Area
                </Typography>
                <List disablePadding data-testid="area-rail">
                  <RailItem
                    to={categoryHref(ALL)}
                    active={selectedCategory === ALL}
                    icon={<AllReportsIcon sx={{ fontSize: 20, color: poolwerxColors.teal }} />}
                    label="All reports"
                    count={matched.length}
                  />
                  {CATEGORY_ORDER.map((cat) => {
                    const count = counts[cat] ?? 0
                    return (
                      <Box key={cat} sx={{ opacity: count === 0 ? 0.5 : 1 }}>
                        <RailItem
                          to={categoryHref(cat)}
                          active={selectedCategory === cat}
                          icon={getCategoryIcon(cat, { size: 20, applyColor: true })}
                          label={cat}
                          count={count}
                        />
                      </Box>
                    )
                  })}
                </List>

                <Divider sx={{ my: 1 }} />
                <Typography variant="overline" sx={RAIL_SECTION_LABEL_SX}>
                  Type
                </Typography>
                <List disablePadding data-testid="type-rail">
                  {typeRailOptions.map(({ value, label, count, color, Icon }) => (
                    <RailItem
                      key={value}
                      to={typeHref(value)}
                      active={selectedType === value}
                      icon={<Icon sx={{ fontSize: 18, color: color ?? 'text.secondary' }} />}
                      label={label}
                      count={count}
                    />
                  ))}
                </List>
              </Paper>
            </Box>

            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
              {visibleSections.length > 0 ? (
                visibleSections.map(([category, rows]) => (
                  <CategorySection key={category} category={category} rows={rows} />
                ))
              ) : selectedCategory === FAVOURITES && favouritesTotal === 0 ? (
                <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                  <FavouritesIcon sx={{ fontSize: 40, mb: 1, opacity: 0.5 }} />
                  <Typography variant="h6" gutterBottom>
                    No favourite reports yet
                  </Typography>
                  <Typography variant="body2">
                    Star reports you use often and they will appear here.
                  </Typography>
                </Box>
              ) : (
                <Alert severity="info">
                  {selectedType !== TYPE_ALL && typeCounts.all > 0
                    ? 'No reports of this type in this category.'
                    : search
                      ? 'No reports match your search.'
                      : 'No reports in this category.'}
                </Alert>
              )}
            </Box>
          </Box>
        </>
      )}

      {reportTypes && reportTypes.length === 0 && (
        <Alert severity="info">No reports available.</Alert>
      )}
    </>
  )
}
